import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Mic, Square, RotateCcw, Volume2, VolumeX, AlertTriangle, ChevronDown } from "lucide-react";

const SCENARIOS = [
  { id: "receptionist", label: "AI Receptionist" },
  { id: "booking", label: "Appointment Booking" },
  { id: "qualification", label: "Lead Qualification" },
  { id: "support", label: "Customer Support" },
  { id: "multilingual", label: "Multilingual Assistant" },
  { id: "enquiries", label: "Business Enquiries" },
];

const LANGUAGES = [
  { id: "en-GB", label: "English" },
  { id: "hi-IN", label: "हिन्दी" },
  { id: "te-IN", label: "తెలుగు" },
];

const SILENCE_MS = 1400;   // end a turn after this much quiet
const MIN_UTTERANCE_MS = 500;

export default function VoiceStudio({ compact = false, scenario, onScenarioChange }) {
  const [session, setSession] = useState(null);
  const [scenarioId, setScenarioId] = useState(scenario || "receptionist");
  const [language, setLanguage] = useState("en-GB");
  const [status, setStatus] = useState("ready"); // ready | connecting | listening | transcribing | thinking | speaking | ended | error
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(true);
  const [newBelow, setNewBelow] = useState(false);  // messages arrived while visitor scrolled up
  const [micLevel, setMicLevel] = useState(0);
  const [turn, setTurn] = useState(0);
  const [maxTurns, setMaxTurns] = useState(12);
  const [muted, setMuted] = useState(false);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const speakingAtRef = useRef(0);
  const lastSpeechAtRef = useRef(0);
  const sessionRef = useRef(null);
  const busyRef = useRef(false);
  const startingRef = useRef(false);   // guards against double-tap duplicate sessions
  const audioRef = useRef(null);
  const mutedRef = useRef(false);
  const statusRef = useRef("ready");
  const transcriptBoxRef = useRef(null);
  const nearBottomRef = useRef(true);

  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { if (scenario) setScenarioId(scenario); }, [scenario]);

  // Transcript auto-scroll: follow new messages only while the visitor is near the
  // bottom; if they scrolled up to read history, show a "new messages" pill instead.
  useEffect(() => {
    const box = transcriptBoxRef.current;
    if (!box) return;
    if (nearBottomRef.current) {
      box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
      setNewBelow(false);
    } else if (transcript.length) {
      setNewBelow(true);
    }
  }, [transcript, showTranscript]);

  const onTranscriptScroll = useCallback(() => {
    const box = transcriptBoxRef.current;
    if (!box) return;
    const dist = box.scrollHeight - box.scrollTop - box.clientHeight;
    nearBottomRef.current = dist < 64;
    if (nearBottomRef.current) setNewBelow(false);
  }, []);

  const jumpToLatest = useCallback(() => {
    const box = transcriptBoxRef.current;
    if (!box) return;
    nearBottomRef.current = true;
    box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
    setNewBelow(false);
  }, []);

  const stopMic = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* already stopped */ }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      const ctx = audioCtxRef.current;
      audioCtxRef.current = null;
      ctx.close().catch(() => {});
    }
  }, []);

  // release the microphone when the tab closes or the component unmounts
  useEffect(() => stopMic, [stopMic]);

  const playReply = useCallback(async (text) => {
    if (mutedRef.current || !sessionRef.current) { setStatus("listening"); return; }
    try {
      const res = await api.post("/voice-studio/speak",
        { session_id: sessionRef.current.session_id, text },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) { audioRef.current.pause(); }
      const el = new Audio(url);
      audioRef.current = el;
      el.onended = () => { URL.revokeObjectURL(url); if (sessionRef.current) setStatus("listening"); };
      el.onerror = () => { URL.revokeObjectURL(url); if (sessionRef.current) setStatus("listening"); };
      setStatus("speaking");
      await el.play();
    } catch {
      if (sessionRef.current) setStatus("listening"); // text reply already visible
    }
  }, []);

  const sendUtterance = useCallback(async (blob) => {
    const sess = sessionRef.current;
    if (!sess || busyRef.current) return;  // busyRef blocks any duplicate/overlapping pipeline run
    busyRef.current = true;
    setStatus("transcribing");
    try {
      const fd = new FormData();
      fd.append("file", blob, "speech.webm");
      fd.append("session_id", sess.session_id);
      const { data: stt } = await api.post("/voice-studio/transcribe", fd);
      const text = (stt.text || "").trim();
      if (!text) { setStatus("listening"); return; }
      setTranscript((t) => [...t, { role: "user", text }]);
      setStatus("thinking");
      const { data: ai } = await api.post("/voice-studio/respond",
        { session_id: sess.session_id, message: text });
      setTurn(ai.turn);
      setTranscript((t) => [...t, { role: "agent", text: ai.reply }]);
      if (ai.turn >= ai.max_turns) {
        setStatus("ended");
        stopMic();
        setError("");
      } else {
        await playReply(ai.reply);
      }
    } catch (e) {
      const statusCode = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (statusCode === 429) {
        setError(typeof detail === "string" ? detail : "The demo is busy — please wait a moment and try again.");
      } else if (statusCode === 410 || statusCode === 404) {
        setError(typeof detail === "string" ? detail : "This demo session has ended — start a new conversation.");
        setStatus("ended");
        stopMic();
        busyRef.current = false;
        return;
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong — please try again.");
      }
      setStatus(sessionRef.current ? "listening" : "error");
    } finally {
      busyRef.current = false;
    }
  }, [playReply, stopMic]);

  const startRecording = useCallback(() => {
    if (!streamRef.current || recorderRef.current) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(streamRef.current);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      recorderRef.current = null;
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      if (blob.size > 800 && sessionRef.current) sendUtterance(blob);
    };
    rec.start(250);
  }, [sendUtterance]);

  const monitor = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    const loop = () => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (let i = 0; i < buf.length; i++) peak = Math.max(peak, Math.abs(buf[i] - 128));
      setMicLevel(peak / 128);
      const now = performance.now();
      const st = statusRef.current;
      if (st === "listening") {
        if (peak > 14) {
          lastSpeechAtRef.current = now;
          if (!recorderRef.current && now - speakingAtRef.current > 400) startRecording();
          speakingAtRef.current = 0;
        } else if (recorderRef.current && now - lastSpeechAtRef.current > SILENCE_MS
                   && now - (recorderRef.current._startedAt || 0) > MIN_UTTERANCE_MS) {
          if (recorderRef.current.state !== "inactive") recorderRef.current.stop();
        }
      }
      if (st === "speaking") speakingAtRef.current = now;
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [startRecording]);

  const start = async () => {
    if (startingRef.current || busyRef.current || ["connecting", "listening", "transcribing", "thinking", "speaking"].includes(statusRef.current)) return;
    startingRef.current = true;
    setError("");
    setStatus("connecting");
    setTranscript([]);
    setTurn(0);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      startingRef.current = false;
      setStatus("error");
      setError("Microphone access is needed for the voice demo. Please allow the microphone in your browser and try again — nothing is recorded until you speak.");
      return;
    }
    try {
      const { data } = await api.post("/voice-studio/session", { scenario: scenarioId, language });
      setSession(data);
      sessionRef.current = data;
      setMaxTurns(data.max_turns);
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      monitor();
      setTranscript([{ role: "agent", text: data.greeting }]);
      await playReply(data.greeting);
      if (statusRef.current !== "ended") setStatus((s) => (s === "speaking" ? s : "listening"));
      startingRef.current = false;
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      startingRef.current = false;
      const detail = e?.response?.data?.detail;
      setStatus("error");
      setError(typeof detail === "string" ? detail : "The voice demo is unavailable right now — please try again later.");
    }
  };

  const end = useCallback(() => {
    stopMic();
    setSession(null);
    sessionRef.current = null;
    setStatus("ended");
  }, [stopMic]);

  const active = !!session && ["connecting", "listening", "transcribing", "thinking", "speaking"].includes(status);
  const STATUS_LABEL = {
    ready: "Ready", connecting: "Connecting…", listening: "Listening", transcribing: "Transcribing…",
    thinking: "Thinking…", speaking: "Speaking", ended: "Ended", error: "Error",
  };
  const orbScale = status === "speaking" ? 1.12 : status === "listening" ? 1 + micLevel * 0.22 : 1;

  return (
    <div className={`overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-[0_24px_60px_-30px_rgba(0,0,0,0.25)] ${compact ? "" : "lg:rounded-[2rem]"}`} data-testid="voice-studio">
      <div className={`grid ${compact ? "" : "lg:grid-cols-2"}`}>
        {/* Left: orb + controls */}
        <div className="relative flex flex-col items-center justify-center px-6 py-10 sm:px-10"
             style={{ background: "linear-gradient(160deg, #fafafa 0%, #efeff1 55%, #e4e4e8 100%)" }}>
          <div className="absolute left-5 top-5 flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${status === "listening" ? "animate-pulse bg-emerald-500" : status === "speaking" ? "animate-pulse bg-sky-500" : status === "error" ? "bg-red-500" : "bg-neutral-300"}`} />
            <span className="text-xs font-semibold text-neutral-500" data-testid="vs-status">{STATUS_LABEL[status]}</span>
          </div>
          <button
            type="button"
            aria-label={active ? "Voice demo active" : "Start conversation"}
            onClick={active ? undefined : start}
            data-testid="vs-orb"
            className="relative mt-4 flex items-center justify-center rounded-full transition-transform duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-neutral-300"
            style={{
              width: compact ? 150 : 190, height: compact ? 150 : 190,
              transform: `scale(${orbScale})`,
              background: "radial-gradient(circle at 32% 28%, #f8fafc 0%, #cbd5e1 34%, #64748b 68%, #1e293b 100%)",
              boxShadow: "0 30px 60px -20px rgba(15,23,42,0.45), inset 0 2px 10px rgba(255,255,255,0.5)",
            }}
          >
            {status === "speaking" && (
              <span className="absolute inset-0 flex items-end justify-center gap-[3px] pb-10" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <span key={i} className="w-[3px] animate-pulse rounded-full bg-white/90"
                        style={{ height: 10 + (i % 3) * 8, animationDelay: `${i * 0.12}s`, animationDuration: "0.7s" }} />
                ))}
              </span>
            )}
            <Mic size={compact ? 30 : 38} className="text-white drop-shadow" />
          </button>

          <div className="mt-7 flex items-center gap-3">
            {!active ? (
              <button
                onClick={start}
                data-testid="vs-start"
                className="rounded-full bg-[#111111] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
              >
                Start Conversation
              </button>
            ) : (
              <button
                onClick={end}
                data-testid="vs-end"
                className="flex items-center gap-2 rounded-full border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-[#111111] transition-colors hover:bg-neutral-50"
              >
                <Square size={13} /> End Conversation
              </button>
            )}
            {active && (
              <button
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute replies" : "Mute replies"}
                data-testid="vs-mute"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-300 bg-white text-neutral-600 transition-colors hover:bg-neutral-50"
              >
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </button>
            )}
            {status === "ended" && (
              <button
                onClick={start}
                data-testid="vs-restart"
                className="flex items-center gap-2 rounded-full bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
              >
                <RotateCcw size={13} /> Restart
              </button>
            )}
          </div>

          <p className="mt-4 text-center text-[11px] leading-relaxed text-neutral-400">
            Your microphone is used only while a conversation is active. Nothing is recorded,
            saved or used to contact you — this is a live browser demo, not a phone call.
          </p>
          {error && (
            <p role="alert" data-testid="vs-error" className="mt-3 flex items-start gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-xs text-red-600">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}
            </p>
          )}
        </div>

        {/* Right: scenario + language + transcript */}
        <div className="border-t border-neutral-100 p-6 sm:p-8 lg:border-l lg:border-t-0">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Scenario</p>
            <span className="text-[11px] text-neutral-400" data-testid="vs-turns">
              {active || status === "ended" ? `Turn ${turn} of ${maxTurns}` : `${maxTurns}-turn demo`}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="vs-scenarios">
            {SCENARIOS.map((s) => (
              <button
                key={s.id}
                onClick={() => { if (!active) { setScenarioId(s.id); onScenarioChange?.(s.id); } }}
                disabled={active}
                aria-pressed={scenarioId === s.id}
                data-testid={`vs-scenario-${s.id}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${scenarioId === s.id ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-500 hover:border-neutral-400"} ${active ? "opacity-50" : ""}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <p className="mt-5 text-xs font-bold uppercase tracking-widest text-neutral-400">Language</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="vs-languages">
            {LANGUAGES.map((l) => (
              <button
                key={l.id}
                onClick={() => { if (!active) setLanguage(l.id); }}
                disabled={active}
                aria-pressed={language === l.id}
                data-testid={`vs-lang-${l.id}`}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${language === l.id ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-500 hover:border-neutral-400"} ${active ? "opacity-50" : ""}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
            data-testid="vs-transcript-toggle"
            className="mt-6 flex items-center gap-1.5 text-xs font-semibold text-neutral-500 transition-colors hover:text-[#111111]"
          >
            Conversation transcript <ChevronDown size={13} className={`transition-transform ${showTranscript ? "rotate-180" : ""}`} />
          </button>
          {showTranscript && (
            <div className="relative mt-3">
              <div
                ref={transcriptBoxRef}
                onScroll={onTranscriptScroll}
                data-testid="vs-transcript"
                className="max-h-64 space-y-2 overflow-y-scroll overscroll-contain rounded-2xl border border-neutral-100 bg-neutral-50/70 p-4"
                style={{ WebkitOverflowScrolling: "touch", scrollPaddingBottom: "0.5rem", touchAction: "pan-y" }}
              >
                {transcript.length === 0 && <p className="text-xs text-neutral-400">Start a conversation to see the transcript here.</p>}
                {transcript.map((m, i) => (
                  <p key={i} className={`max-w-[90%] whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-xs leading-relaxed ${m.role === "agent" ? "ml-auto bg-[#111111] text-white" : "bg-white text-neutral-700"}`}>
                    <span className="mr-1.5 text-[9px] font-bold uppercase tracking-wider opacity-60">{m.role === "agent" ? "AI" : "You"}</span>
                    {m.text}
                  </p>
                ))}
                {status === "ended" && transcript.length > 0 && (
                  <p className="pt-1 text-center text-[10px] font-semibold uppercase tracking-widest text-neutral-400" data-testid="vs-transcript-ended">
                    Conversation ended — start a new conversation to continue
                  </p>
                )}
              </div>
              {newBelow && (
                <button
                  onClick={jumpToLatest}
                  data-testid="vs-jump-latest"
                  className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-[#111111] px-3 py-1 text-[10px] font-semibold text-white shadow-lg transition-colors hover:bg-neutral-800"
                >
                  New messages ↓
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
